
import { Component, Vue, Prop } from 'vue-property-decorator';
import { axios } from '@/helpers/axios';
import { AxiosResponse } from 'axios';
import ChartHeading from '@/components/DataTab/Charts/ChartComponents/ChartHeading.vue';
import ChartComponent from '@/components/DataTab/Charts/ChartComponents/ChartComponent.vue';
import {
  ChartLoadingStatus,
  DemandDataType,
  PlotlyTraceType,
  ScenarioNameType,
} from '@/types';
import { breakBetweenWords } from '@/helpers/txtwrapper';
import { colors } from '@/assets/colors/colors';

@Component({
  components: { ChartComponent, ChartHeading },
})
export default class DemandVsDemandMetChart extends Vue {
  @Prop() readonly areaId!: number;
  @Prop() readonly chartId!: number;

  chartData: DemandDataType[] = [];
  chartSelection: 'max' | 'avg' = 'max';
  dataStatus: ChartLoadingStatus = 'idle';
  mounted() {
    this.dataStatus = 'loading';
    axios
      .get(`/charts/${this.chartId}/${this.areaId}`)
      .then((response: AxiosResponse) => {
        this.chartData = response.data.data;
        this.dataStatus = 'success';
      })
      .catch(() => (this.dataStatus = 'error'));
  }

  get description() {
    return this.$t(`data.chartTables.${this.chartId}.description`);
  }

  get scenarioList(): ScenarioNameType[] {
    return this.$store.getters.selectedScenarioList;
  }

  get plotlyData() {
    const demandLabel = this.$t(
      `data.chartTables.${this.chartId}.labels.demand`,
    );

    const demandVals: PlotlyTraceType = {
      x: [],
      y: [],
      name: demandLabel,
      type: 'scatter',
      mode: 'markers',
      marker: {
        color: colors.purple,
        symbol: 'square',
        size: 10,
      },
      hovertemplate: `<b>${demandLabel}:</b> %{y:,.2f}<extra></extra>`,
    };
    const demandMetLabel = this.$t(
      `data.chartTables.${this.chartId}.labels.demandMet`,
    );
    const demandMetVals: PlotlyTraceType = {
      x: [],
      y: [],
      name: demandMetLabel,
      type: 'scatter',
      mode: 'markers',
      marker: {
        color: colors.teal,
        symbol: 'triangle-up',
        size: 12,
      },
      hovertemplate: `<b>${demandMetLabel}:</b> %{y:,.2f}<extra></extra>`,
    };

    const gaps: PlotlyTraceType[] = [];

    this.scenarioList.forEach((scenario, index) => {
      this.chartData.forEach(dataElement => {
        if (dataElement.scenario === scenario) {
          demandVals.x.push(scenario);
          demandMetVals.x.push(scenario);
          demandVals.y.push(
            this.chartSelection === 'max'
              ? dataElement.max_demand
              : dataElement.avg_demand,
          );
          demandMetVals.y.push(
            this.chartSelection === 'max'
              ? dataElement.max_demand_met
              : dataElement.avg_demand_met,
          );
          gaps.push({
            x: [scenario, scenario],
            y: [
              this.chartSelection === 'max'
                ? dataElement.max_demand_met
                : dataElement.avg_demand_met,
              this.chartSelection === 'max'
                ? dataElement.max_demand
                : dataElement.avg_demand,
            ],
            name: 'Gap',
            type: 'scatter',
            mode: 'lines',
            showlegend: index === 0 ? true : false, // show only the first gap in the legend
            marker: {
              color: 'grey',
              size: 1,
            },
            line: {
              dash: 'dot',
            },
          });
        }
      });
    });
    const traces = [demandVals, demandMetVals];
    const allTraces = gaps.concat(traces);

    return allTraces;
  }

  get layout() {
    const unit = this.$t(`data.chartTables.${this.chartId}.unit`);
    const label = this.$t(`data.chartTables.${this.chartId}.axisLabels.y`);
    return {
      xaxis: {
        fixedrange: true,
        tickmode: 'array',
        tickvals: this.scenarioList,
        ticktext: this.scenarioList.map(word => breakBetweenWords(word)),
        tickfont: {
          size: this.$vuetify.breakpoint.smAndDown
            ? 8
            : this.$vuetify.breakpoint.mdAndDown
            ? 9
            : 10,
        },
        tickangle: this.$vuetify.breakpoint.mdAndDown ? 90 : 0,
      },
      yaxis: {
        fixedrange: true,
        automargin: true,
        title: {
          text: `${label} (${unit})`,
          standoff: 10,
        },
        hoverformat: '.2f',
      },
      hovermode: 'closest',
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      legend: {
        x: 0,
        y: -0.35,
        orientation: 'h',
        traceorder: 'reversed',
      },
    };
  }
}
